import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuIcon } from '../icons/menu.icon';
import { CalendarIcon } from '../icons/calendar.icon';
import { HighlightIcon } from '../icons/highlight.icon';
import { MyTasksIcon } from '../icons/myTasks.icon';

interface ViewOptionsMenuProps {
  isDayView: boolean;
  highlightMyTasks: boolean;
  showOnlyMyTasks: boolean;
  onViewChange: ( checked: boolean ) => void;
  onHighlightMyTasks: ( checked: boolean ) => void;
  onShowOnlyMyTasks: ( checked: boolean ) => void;
}

export const ViewOptionsMenu = ( {
  isDayView,
  highlightMyTasks,
  showOnlyMyTasks,
  onViewChange,
  onHighlightMyTasks,
  onShowOnlyMyTasks
}: ViewOptionsMenuProps ) => {
  const { t } = useTranslation();
  const [ isMenuOpen, setIsMenuOpen ] = useState<boolean>( false );

  return (
    <div className="relative">
      <button
        onClick={() => { setIsMenuOpen( !isMenuOpen ); }}
        className="flex items-center justify-center w-10 h-10 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors"
        aria-label="Options d'affichage"
      >
        <MenuIcon />
      </button>

      {/* Menu déroulant */}
      {isMenuOpen && (
        <div className="absolute right-0 mt-2 w-fit bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-2">
            {/* Planning journée */}
            <div className="flex items-center justify-between p-3 space-x-3 hover:bg-gray-50 rounded-md">
              <div className="flex items-center space-x-3">
                <CalendarIcon />
                <span className="text-sm font-medium text-gray-900">{ t( 'common:day' ) }</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDayView}
                  onChange={e => { onViewChange( e.target.checked ); }}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Divider */}
            <hr className="my-1 border-gray-200" />

            {/* Mettre en évidence mes missions */}
            <div className="flex items-center justify-between p-3 space-x-3 hover:bg-gray-50 rounded-md">
              <div className="flex items-center space-x-3">
                <HighlightIcon />
                <span className="text-sm font-medium text-gray-900">{ t( 'translation:highlight_my_tasks' ) }</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={highlightMyTasks}
                  onChange={e => { onHighlightMyTasks( e.target.checked ); }}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-yellow-500"></div>
              </label>
            </div>

            {/* Divider */}
            <hr className="my-1 border-gray-200" />

            {/* Présenter mes missions uniquement */}
            <div className="flex items-center justify-between p-3 space-x-3 hover:bg-gray-50 rounded-md">
              <div className="flex items-center space-x-3">
                <MyTasksIcon />
                <span className="text-sm font-medium text-gray-900">{ t( 'translation:my_tasks_only' ) }</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showOnlyMyTasks}
                  onChange={e => { onShowOnlyMyTasks( e.target.checked ); }}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Overlay pour fermer le menu en cliquant ailleurs */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { setIsMenuOpen( false ); }}
        ></div>
      )}
    </div>
  );
};
